import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Wallet,
  TrendingUp,
  Activity,
  PiggyBank,
  Plus,
  Minus,
  Copy,
  Sparkles,
  Zap,
} from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Sparkline } from "../components/dashboard/Charts";
import { watchlist } from "../dashboardMock";
import { useAuth } from "../context/AuthContext";
import { useCopies } from "../hooks/useCopies";
import { traders } from "../copyTradingMock";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bb-card rounded-2xl p-5 lg:p-6 relative overflow-hidden">
    <div
      className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-50"
      style={{ background: accent || "#1c3df533" }}
    ></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.05] border border-white/8">
          <Icon size={17} className="text-white/80" strokeWidth={1.8} />
        </div>
      </div>
      <div className="text-[12px] text-white/55 mb-1">{label}</div>
      <div className="font-display text-[26px] lg:text-[30px] font-bold text-white tabular-nums leading-none">
        {value}
      </div>
      {sub && <div className="text-[12px] text-white/45 mt-2">{sub}</div>}
    </div>
  </div>
);

const Overview = () => {
  const { user } = useAuth();
  const { copies, totalAllocated, totalValue, totalPnL, totalReturnPct } = useCopies();
  const topTraders = traders.slice().sort((a, b) => b.totalReturn - a.totalReturn).slice(0, 3);

  const firstName = (user?.name || "trader").trim().split(" ")[0] || "trader";
  const balance = Number(user?.balance || 0);
  // Free funds = balance not locked in copy trades
  const freeMargin = Math.max(0, balance - totalAllocated);
  // Equity = cash balance + current value of open copy positions
  const equity = freeMargin + totalValue;
  const accountId = user?.id ? `BB-${user.id.slice(0, 8).toUpperCase()}` : "—";
  const isNew = balance <= 0 && copies.length === 0;

  return (
    <DashboardLayout title="Overview" subtitle={`Welcome${isNew ? "" : " back"}, ${firstName}`}>
      {/* Empty-state banner for brand-new accounts */}
      {isNew && (
        <div className="bb-card rounded-2xl p-5 lg:p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-gradient-to-br from-emerald-400/30 to-transparent blur-3xl"></div>
          <div className="relative flex items-start gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-display font-bold text-[20px] text-white">Welcome to Northwind, {firstName} 👋</span>
                {!user?.bonus_credited && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-400/25 border border-emerald-400/40 rounded text-emerald-200 font-semibold">
                    100% bonus
                  </span>
                )}
              </div>
              <div className="text-[13px] text-white/65 leading-snug max-w-xl">
                {!user?.bonus_credited
                  ? "Deposit $20 or more on your first deposit and we'll instantly match it 100% — double your trading balance."
                  : "Your account is ready. Make a deposit to start trading, or copy a top trader to mirror their moves automatically."}
              </div>
            </div>
          </div>
          <Link
            to="/dashboard/deposits"
            className="relative inline-flex items-center gap-1.5 px-4 py-2.5 bb-blue text-white text-[13px] font-medium rounded-lg shrink-0"
          >
            <Plus size={14} /> Claim bonus
          </Link>
        </div>
      )}

      {/* KPI Row — real data */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={Wallet}
          label="Account Balance"
          value={`$${formatMoney(balance)}`}
          sub="USD · Lev 1:500"
          accent="#1c3df533"
        />
        <KpiCard
          icon={Activity}
          label="Equity"
          value={`$${formatMoney(equity)}`}
          sub={`Free margin $${formatMoney(freeMargin)}`}
          accent="#22c55e33"
        />
        <KpiCard
          icon={TrendingUp}
          label="Copy P&L"
          value={`${totalPnL >= 0 ? "+" : ""}$${formatMoney(totalPnL)}`}
          sub={copies.length > 0 ? `${copies.length} active trader${copies.length === 1 ? "" : "s"}` : "No active copies"}
          accent="#22c55e33"
        />
        <KpiCard
          icon={PiggyBank}
          label="Total return"
          value={totalAllocated > 0 ? `${totalReturnPct >= 0 ? "+" : ""}${totalReturnPct.toFixed(2)}%` : "—"}
          sub={totalAllocated > 0 ? `On $${formatMoney(totalAllocated)} allocated` : "Start copying to see returns"}
          accent="#a855f733"
        />
      </div>

      {/* Copy Trading strip */}
      {copies.length > 0 ? (
        <Link
          to="/dashboard/copy-trading"
          className="bb-card rounded-2xl p-5 lg:p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative overflow-hidden group hover:border-[#1c3df5]/30 transition-all"
        >
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-gradient-to-br from-[#1c3df5]/25 to-transparent blur-3xl"></div>
          <div className="relative flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center shrink-0">
              <Copy size={20} className="text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] uppercase tracking-wider text-white/55 font-semibold">Copy trading</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              </div>
              <div className="font-display font-bold text-[20px] text-white tabular-nums">
                ${formatMoney(totalValue)}
              </div>
              <div
                className={`text-[12px] tabular-nums font-medium ${
                  totalPnL >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {totalPnL >= 0 ? "+" : ""}${formatMoney(totalPnL)} ({totalPnL >= 0 ? "+" : ""}{totalReturnPct.toFixed(2)}%) · {copies.length} trader{copies.length > 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <div className="relative hidden md:flex -space-x-2">
            {copies.slice(0, 4).map((c) => (
              <div
                key={c.id}
                className="w-9 h-9 rounded-full border-2 border-[#0b0c14] flex items-center justify-center text-[11px] font-bold text-white"
                style={{ background: c.traderColor }}
              >
                {c.traderAvatar}
              </div>
            ))}
          </div>
          <ArrowUpRight size={18} className="relative text-white/55 group-hover:text-white transition-colors shrink-0" />
        </Link>
      ) : (
        <Link
          to="/dashboard/copy-trading"
          className="bb-card rounded-2xl p-5 lg:p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative overflow-hidden group hover:border-[#1c3df5]/30 transition-all"
        >
          <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-gradient-to-br from-[#1c3df5]/30 to-transparent blur-3xl"></div>
          <div className="relative flex items-start gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display font-bold text-[18px] text-white">Copy top traders</span>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1c3df5]/20 border border-[#1c3df5]/40 rounded text-[#7d8eff] font-semibold">NEW</span>
              </div>
              <div className="text-[13px] text-white/60 leading-snug">
                Mirror Oringo AI (200%/mo), CryptoNinja (150%/mo), GoldRush Pro (80%/mo) and more — trades auto-copy to your account.
              </div>
            </div>
          </div>
          <div className="relative flex items-center gap-3">
            <div className="hidden md:flex -space-x-2">
              {topTraders.map((t) => (
                <div
                  key={t.id}
                  className="w-9 h-9 rounded-full border-2 border-[#0b0c14] flex items-center justify-center text-[11px] font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${t.color}, ${t.accent})` }}
                >
                  {t.avatar}
                </div>
              ))}
            </div>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 bb-blue text-white text-[13px] font-medium rounded-lg shrink-0">
              <Zap size={14} /> Explore
            </span>
          </div>
        </Link>
      )}

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5 mb-6">
        {/* Quick actions — full width when there's no equity chart */}
        <div className="lg:col-span-2 bb-card rounded-2xl p-5 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold text-[16px] text-white">Account snapshot</div>
            <div className="text-[11px] text-white/45 font-mono">{accountId}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-[11px] text-white/45 mb-0.5">Cash balance</div>
              <div className="text-[15px] text-white font-semibold tabular-nums">${formatMoney(balance)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45 mb-0.5">In copy trades</div>
              <div className="text-[15px] text-white font-semibold tabular-nums">${formatMoney(totalValue)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45 mb-0.5">Available to use</div>
              <div className="text-[15px] text-white font-semibold tabular-nums">${formatMoney(freeMargin)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45 mb-0.5">KYC status</div>
              <div className="text-[13px] text-white font-medium capitalize">
                {user?.kyc_status || "unverified"}
              </div>
            </div>
          </div>

          {user?.kyc_status !== "verified" && (
            <Link
              to="/dashboard/settings"
              className="mt-5 flex items-center justify-between px-4 py-3 rounded-xl bg-amber-400/8 border border-amber-400/25 hover:bg-amber-400/15 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center text-amber-300">
                  <Sparkles size={15} />
                </div>
                <div>
                  <div className="text-[13px] text-white font-medium">Verify your identity</div>
                  <div className="text-[11px] text-white/55">
                    Required for withdrawals above $200 — takes 2 mins.
                  </div>
                </div>
              </div>
              <ArrowUpRight size={15} className="text-white/55" />
            </Link>
          )}
        </div>

        {/* Quick actions */}
        <div className="bb-card rounded-2xl p-5 lg:p-6">
          <div className="text-[12px] text-white/55 mb-4">Quick actions</div>
          <div className="space-y-2">
            <Link
              to="/dashboard/deposits"
              className="flex items-center justify-between px-4 py-3.5 rounded-xl bg-[#1c3df5]/12 border border-[#1c3df5]/25 hover:bg-[#1c3df5]/20 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1c3df5] flex items-center justify-center">
                  <Plus size={16} />
                </div>
                <div>
                  <div className="text-[14px] font-medium text-white">Deposit funds</div>
                  <div className="text-[11px] text-white/55">Crypto or bank transfer</div>
                </div>
              </div>
              <ArrowUpRight size={15} className="text-white/70" />
            </Link>
            <Link
              to="/dashboard/deposits"
              className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-white/8 hover:bg-white/[0.04] transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/8 flex items-center justify-center">
                  <Minus size={16} />
                </div>
                <div>
                  <div className="text-[14px] font-medium text-white">Withdraw</div>
                  <div className="text-[11px] text-white/55">Available ${formatMoney(freeMargin)}</div>
                </div>
              </div>
              <ArrowUpRight size={15} className={freeMargin > 0 ? "text-white/70" : "text-white/30"} />
            </Link>
            <Link
              to="/dashboard/copy-trading"
              className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-white/8 hover:bg-white/[0.04] transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/8 flex items-center justify-center">
                  <Copy size={16} />
                </div>
                <div>
                  <div className="text-[14px] font-medium text-white">Copy a trader</div>
                  <div className="text-[11px] text-white/55">From $10</div>
                </div>
              </div>
              <ArrowUpRight size={15} className="text-white/40" />
            </Link>
          </div>
        </div>
      </div>

      {/* Watchlist only — open positions removed since trade engine isn't live yet */}
      <div className="bb-card rounded-2xl p-5 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold text-[16px] text-white">Market watchlist</div>
          <Link to="/dashboard/trade" className="text-[12px] text-[#7d8eff] hover:underline">
            Open terminal →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-1">
          {watchlist.slice(0, 8).map((w) => {
            const up = w.change >= 0;
            return (
              <Link
                key={w.symbol}
                to="/dashboard/trade"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/8 flex items-center justify-center text-[11px] font-bold text-white">
                  {w.symbol.slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white font-medium">{w.symbol}</div>
                  <div className="text-[11px] text-white/45 truncate">{w.name}</div>
                </div>
                <div className="hidden sm:block">
                  <Sparkline data={w.spark} color="auto" width={70} height={22} />
                </div>
                <div className="text-right min-w-[80px]">
                  <div className="text-[13px] text-white tabular-nums">{w.bid}</div>
                  <div
                    className={`text-[11px] tabular-nums font-medium ${
                      up ? "text-emerald-400" : "text-rose-400"
                    }`}
                  >
                    {up ? "+" : ""}
                    {w.change}%
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Overview;
